export class RequestTimeoutError extends Error {
    constructor() {
        super("Die Anfrage dauert zu lange. Bitte erneut versuchen.");
        this.name = "RequestTimeoutError";
    }
}

/** Bounds both connection and body reading, even if a transport ignores abort. */
export async function withRequestDeadline<T>(
    operation: (signal: AbortSignal) => Promise<T>,
    {signal, timeoutMs = 15000}: {signal?: AbortSignal; timeoutMs?: number} = {},
): Promise<T> {
    if (signal?.aborted) throw signal.reason ?? new DOMException("Aborted", "AbortError");
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancel: (() => void) | undefined;
    const cancelled = new Promise<never>((_, reject) => {
        cancel = () => {
            const reason = signal?.reason ?? new DOMException("Aborted", "AbortError");
            reject(reason);
            controller.abort(reason);
        };
        signal?.addEventListener("abort", cancel, {once: true});
        timer = setTimeout(() => {
            const reason = new RequestTimeoutError();
            reject(reason);
            controller.abort(reason);
        }, timeoutMs);
    });
    try {
        return await Promise.race([operation(controller.signal), cancelled]);
    } finally {
        clearTimeout(timer);
        if (cancel) signal?.removeEventListener("abort", cancel);
    }
}
